/**
 * Valuation (migrada do Streamlit; equivalência em backend/tests_paginas/test_valuation.py).
 *
 * Modo, ativos (separados por vírgula), período (padrão 10A, ou a data inicial no
 * Personalizado) e média móvel ficam na URL.
 * mm = 0 significa sem média móvel.
 */
import { Alert, Loader, MultiSelect, NumberInput, Select, Switch } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { Elementos, type ElementoApi } from '../../components/Elementos';
import {
  BarraFiltros,
  CampoOpcoes,
  CampoPeriodo,
  GrupoFiltros,
  LARGURA,
} from '../../components/Filtros';
import { opcoesPeriodo, type OpcaoPeriodo } from '../../lib/periodos';
import { useFiltrosUrl } from '../../lib/url-state';

const MM_PADRAO = 21;

export function separarAtivos(texto: string): string[] {
  return texto
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean);
}

// Usados enquanto a primeira resposta não chega; a API devolve a lista definitiva.
const PERIODOS = opcoesPeriodo(['3a', '5a', '10a', 'max']);

interface DadosValuation {
  inicio: string;
  periodos: OpcaoPeriodo[];
  elementos: ElementoApi[];
}

export function Valuation() {
  const [filtros, definir] = useFiltrosUrl({
    modo: 'unico',
    ativos: '',
    periodo: '10a',
    inicio: '',
    mm: 0,
  });
  const unico = filtros.modo === 'unico';
  const ativos = separarAtivos(filtros.ativos);
  const pronto = unico ? ativos.length === 1 : ativos.length >= 2;

  const lista = useQuery({
    queryKey: ['equities.valuation.ativos'],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ ativos: string[] }>>('/equities/valuation/ativos', {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const opcoes = lista.data?.data.ativos ?? [];

  const consulta = useQuery({
    queryKey: [
      'equities.valuation',
      filtros.modo,
      ativos,
      filtros.periodo,
      filtros.inicio,
      filtros.mm,
    ],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosValuation>>(
        '/equities/valuation',
        {
          modo: filtros.modo,
          ativos,
          periodo: filtros.periodo,
          inicio: filtros.inicio,
          mm: filtros.mm > 0 ? filtros.mm : undefined,
        },
        signal,
      ),
    enabled: pronto,
    placeholderData: keepPreviousData,
  });

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Valuation"
        descricao="Múltiplos forward (P/E e EV/EBITDA) e estimativas de lucro e EBITDA de uma ação, ou o comparativo entre ações."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Banco Persevera (factor_zoo) · Fibery (ações ativas)"
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo="Ativos">
          <CampoOpcoes
            rotulo="Modo"
            valor={filtros.modo}
            aoMudar={(modo) => definir({ modo, ativos: '' })}
            opcoes={[
              { valor: 'unico', rotulo: 'Ativo único' },
              { valor: 'comparativo', rotulo: 'Comparativo' },
            ]}
          />
          {unico ? (
            <Select
              label="Ativo"
              placeholder="Selecione um ativo"
              searchable
              data={opcoes}
              value={ativos[0] ?? null}
              onChange={(ativo) => definir({ ativos: ativo ?? '' })}
              w={LARGURA.selecao}
            />
          ) : (
            <MultiSelect
              label="Ativos"
              placeholder="Selecione os ativos"
              searchable
              data={opcoes}
              value={ativos}
              onChange={(escolhidos) => definir({ ativos: escolhidos.join(',') })}
              miw={LARGURA.selecao}
              maw={560}
            />
          )}
        </GrupoFiltros>
        <GrupoFiltros titulo="Período">
          <CampoPeriodo
            periodo={filtros.periodo}
            inicio={filtros.inicio}
            inicioEfetivo={consulta.data?.data.inicio}
            opcoes={consulta.data?.data.periodos ?? PERIODOS}
            min="2010-01-01"
            aoMudar={definir}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Média móvel">
          <Switch
            label="Ativa"
            checked={filtros.mm > 0}
            onChange={(e) => definir({ mm: e.currentTarget.checked ? MM_PADRAO : 0 })}
            // Grupos alinham pelo topo: desce o interruptor até a altura das caixas.
            mt={30}
          />
          <NumberInput
            label="Janela (dias)"
            min={2}
            max={252}
            // Desligada, a janela fica visível (e desabilitada) para o layout não pular.
            disabled={filtros.mm === 0}
            value={filtros.mm > 0 ? filtros.mm : MM_PADRAO}
            onChange={(v) => typeof v === 'number' && v >= 2 && v <= 252 && definir({ mm: v })}
            w={LARGURA.numero}
          />
        </GrupoFiltros>
      </BarraFiltros>

      {lista.isError && (
        <Alert color="red">
          {lista.error instanceof ErroApi ? lista.error.message : 'Falha ao carregar os ativos.'}
        </Alert>
      )}

      {!pronto && (
        <Alert color="blue">
          {unico
            ? 'Selecione um ativo para visualizar as métricas de valuation.'
            : 'Selecione pelo menos dois ativos para o comparativo.'}
        </Alert>
      )}

      {pronto && consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi ? consulta.error.message : 'Erro ao carregar dados.'}
        </Alert>
      )}
      {pronto && consulta.isPending && <Loader />}
      {pronto && consulta.data && (
        <>
          <AvisosApi avisos={[consulta.data.meta.avisos]} />
          <Secao
            titulo={unico ? `Múltiplos de ${ativos[0]}` : 'Comparativo de múltiplos'}
            legenda={
              unico
                ? 'Múltiplo forward (linha escura, eixo esquerdo) contra a estimativa de lucro ou EBITDA (eixo direito).'
                : 'Mesmo múltiplo forward para cada ativo escolhido.'
            }
          >
            <Elementos elementos={consulta.data.data.elementos} />
          </Secao>
        </>
      )}
    </Pagina>
  );
}
